import { z } from 'zod';
import { uuid } from './common.js';
/**
 * Ausrüstungs-Angebote ("Equipment"): Kommissionsware (Schläger, Schuhe,
 * Kleidung, Besaitung), die Mitglieder beim Verein testen können und die über
 * den Verein vermittelt wird. `priceCents` ist Pflicht (Preis in Cent, um
 * Rundungsprobleme bei Fließkommazahlen zu vermeiden), siehe
 * docs/equipment.md. Je Größe lässt sich außerdem eine Bestandsmenge angeben
 * (`EquipmentItemSize`) — z. B. wie viele Trikots von welcher Größe noch
 * vorhanden sind. Mehrere Produktbilder je Angebot sind möglich
 * (`EquipmentItemImageResponse`); `coverImageId` ist vom Server immer
 * aufgelöst (gewähltes Titelbild, sonst das älteste Bild, sonst `null`) —
 * das Frontend braucht keine eigene Fallback-Logik.
 */
// Reihenfolge bestimmt die Tab-Reihenfolge auf der Equipment-Seite (§
// Nutzeranfrage „Bälle soll als erstes erscheinen") — `balls` bewusst zuerst,
// da das die einzige tatsächlich verkaufte Rubrik ist (siehe
// `SELLABLE_EQUIPMENT_CATEGORIES` unten).
export const EQUIPMENT_CATEGORIES = ['balls', 'racket', 'shoes', 'clothing', 'stringing'];
export const EQUIPMENT_CATEGORY_LABELS = {
    racket: 'Schläger',
    shoes: 'Schuhe',
    clothing: 'Kleidung',
    stringing: 'Besaitung',
    balls: 'Bälle',
};
/** Anders als die übrigen Rubriken (reine Testware) stehen Produkte unter
 * `balls` tatsächlich zum Verkauf — nur für diese Rubrik zeigt das Frontend
 * den PayPal-Button + die Verkaufsliste (§ Nutzeranfrage). */
export const SELLABLE_EQUIPMENT_CATEGORIES = ['balls'];
/** Eine Größe + wie viele Stück davon noch vorhanden sind. `size` bleibt
 * Freitext (siehe `equipmentItemWritable`s Kommentar) — nur die Menge ist
 * strukturiert. */
export const equipmentItemSize = z.object({
    size: z.string().trim().min(1).max(40),
    quantity: z.number().int().min(0).max(99_999),
});
export const equipmentItemWritable = z.object({
    category: z.enum(EQUIPMENT_CATEGORIES),
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().max(5000).nullable().optional(),
    // Bewusst kein strukturiertes Größensystem (Enum o. ä.) für `size` selbst:
    // Schläger-„Größen" sind Griffstärken, Schuhe EU-Größen, Kleidung meist
    // S/M/L — nur die Menge je Größe ist strukturiert. Mehrfache Einträge mit
    // derselben Größe wären nur verwirrend (welcher Bestand gilt?), deshalb
    // hier abgelehnt statt sie stillschweigend nebeneinander stehen zu lassen.
    sizes: z
        .array(equipmentItemSize)
        .max(50)
        .optional()
        .refine((sizes) => !sizes || new Set(sizes.map((s) => s.size)).size === sizes.length, {
        message: 'Jede Größe darf nur einmal vorkommen.',
    }),
    priceCents: z.number().int().nonnegative(),
});
export const createEquipmentItemRequest = equipmentItemWritable;
export const updateEquipmentItemRequest = equipmentItemWritable.partial();
export const equipmentListQuery = z.object({
    category: z.enum(EQUIPMENT_CATEGORIES).optional(),
});
// ── Bälle: Verkauf über PayPal (§ Nutzeranfrage) ────────────────────────────
//
// PayPal.me-Links unterstützen keine Rückgabe-/Beschreibungs-Parameter — der
// Preis eines Produkts (`priceCents`) reicht, um daraus den passenden
// `paypal.me/<handle>/<betrag>EUR`-Link automatisch zu bauen, ein Link pro
// Produkt muss nicht separat gepflegt werden. Ob eine Zahlung tatsächlich
// abgeschlossen wurde, erfährt der Server erst über PayPals „Instant Payment
// Notification" (IPN) — ein Kaufversuch ist deshalb zunächst `pending` und
// wird erst beim passenden IPN-Aufruf zu `confirmed` (siehe
// `apps/api/src/equipment/paypal-ipn.controller.ts` und docs/equipment.md).
/** PUT /equipment/paypal-settings (`equipment.manage`) — ein Konto pro
 * Verein, nicht pro Produkt. */
export const equipmentPaypalSettingsWritable = z.object({
    paypalMeHandle: z
        .string()
        .trim()
        .min(1)
        .max(100)
        // paypal.me-Handles sind alphanumerisch (+ ein paar Sonderzeichen), aber
        // keine volle URL — ein versehentlich eingefügtes "https://paypal.me/"
        // davor würde den gebauten Link kaputt machen, daher hier abgefangen.
        .refine((v) => !v.includes('/') && !v.includes(' '), {
        message: 'Nur der Handle-Name, ohne „paypal.me/" davor (z. B. „SCStToenis").',
    }),
    paypalEmail: z.string().trim().email().max(200),
});
export const EQUIPMENT_PURCHASE_STATUSES = ['pending', 'confirmed', 'cancelled'];
export const equipmentPurchaseListQuery = z.object({
    equipmentItemId: uuid.optional(),
});
/** PATCH /equipment/purchases/:id (`equipment.manage`) — manuelle Korrektur,
 * da die Zuordnung (welcher Kauf gehört zu welcher Zahlung) auf einer
 * Betrags-Heuristik beruht und in Randfällen (zwei gleich teure Produkte
 * fast gleichzeitig gekauft, Zahlung ganz ohne vorherigen Klick, …) vom
 * Admin nachgebessert werden können muss. */
export const updateEquipmentPurchaseRequest = z.object({
    status: z.enum(EQUIPMENT_PURCHASE_STATUSES).optional(),
    memberId: uuid.nullable().optional(),
    equipmentItemId: uuid.nullable().optional(),
});
//# sourceMappingURL=equipment.js.map